import { idbBackend } from './idb';
import { memoryBackend } from './memory';
import { opfsBackend } from './opfs';
import { fileStore, type FileStore } from './store';

export { fileStore } from './store';
export type { FileStore, StoredFile } from './store';
export { memoryBackend } from './memory';
export { canShareFiles, saveFile, shareFiles, toShareFiles } from './share';
export { buildPdfFromPhotos } from './photoPdf';
export { openPdf, pageCountOf } from './pdfPages';
export { isWordDocument, readWordDocument } from './word';
export type { PdfPages } from './pdfPages';
export { mergeDocuments } from './pdfMerge';
export { findPage, flattenPage, clockwise, photoSize } from './scan';
export type { Corner, Quad, ScannedPage } from './scan';
export type { MergePart, MergedPdf } from './pdfMerge';
export type { PhotoPdf } from './photoPdf';
export { orientationOf, sizeOf, transformFor } from './exif';
export type { ShareOutcome, ShareTarget } from './share';

/**
 * Умеет ли OPFS **писать**.
 *
 * Спрашивать `getDirectory` мало, и это стоило партнёру целого дня работы: Safari
 * отдаёт корень OPFS с iOS 15.2, но метод, которым в файл кладут байты,
 * `createWritable`, появился только в Safari 26. На всех iPhone между этими версиями
 * ветка выбиралась «настоящими файлами», а первая же запись падала на
 * `createWritable is not a function` — юрист видел «Не удалось добавить документ»
 * и ни одного документа в деле, пока дела, клиенты и стадии работали (база живёт
 * на синхронных ручках, они в Safari есть).
 *
 * Отсюда правило: ветка проверяется по тому методу, которым пользуются, а не по
 * соседнему. У базы это сделано так же — `hasOpfsAccessHandles` в `db/open.ts`.
 */
function canWriteToOpfs(): boolean {
  try {
    if (!navigator.storage?.getDirectory) return false;
    // Наличие метода в прототипе — единственная проверка до открытия файла.
    return typeof FileSystemFileHandle.prototype.createWritable === 'function';
  } catch {
    return false;
  }
}

/**
 * Открывает файловое хранилище приложения.
 *
 * Ветка выбирается так же, как у базы ([D-50](docs/DECISIONS.md)): OPFS умеет писать —
 * файлы лежат файлами; не умеет (iPhone до iOS 26 и всё, что старше) — тем же путём
 * в IndexedDB. Для всего, что выше, разницы нет: пути, размеры и уборка одни и те же.
 */
export async function openFileStore(): Promise<FileStore> {
  if (canWriteToOpfs()) {
    try {
      const root = await navigator.storage.getDirectory();
      if (root) return fileStore(opfsBackend(root));
    } catch {
      // OPFS есть в типах, но не в браузере — переходим к запасному пути.
    }
  }

  return fileStore(idbBackend());
}

/** Хранилище в памяти — для тестов. */
export const openInMemoryFileStore = (): FileStore => fileStore(memoryBackend());
